import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Mine Uğur — a contact page for one person.
 *
 * Asked for 2026-09-15 with the address already chosen:
 * `/en/about/team/mine-ugur`, "professional and appropriate", and three facts —
 * the name, the mobile number and the email address. Nothing else was supplied,
 * so nothing else is claimed here: no job title, no biography and no portrait,
 * because a page about a person that invents her role is worse than a page that
 * only gives you a way to reach her. The `check` on the hero asks for the title
 * and a photograph; both drop straight into this file when they arrive.
 *
 * The details are links rather than typed-out strings — a phone number on a
 * phone should dial, and an address should open a mail window.
 *
 * Asked for 2026-09-15: the team contact pages should read as one set, so this
 * page follows the shared shape — a centred name, a `features` grid whose card
 * title *is* the address or the number and is itself the link, and the closing
 * band back to the firm that every other card ends on. The eyebrow stays
 * "Digitopia" rather than a role, because no role was given; the `check` below
 * is still the one asking for it.
 */
export const route: RouteDef = {
  id: 'teamMineUgur',
  path: 'about/team/mine-ugur',
  // Asked for 2026-09-15 for the other contact cards and applied here for the
  // same reason: they are handed out as QR codes and links, so nothing on the
  // site should lead to them — out of the sitemap and out of search results.
  // The URL itself still works.
  robots: 'noindex',
  priority: 0.5,
}

const EMAIL = 'mine@digitopia.ai'
const PHONE = '+90 532 514 73 94'
const PHONE_HREF = 'tel:+905325147394'
// Asked for 2026-09-17, for every team page: a button that saves the details
// straight into the reader's phone. The file is a static vCard in
// `public/vcf/`, named after this page's slug — see that folder's README.
const VCARD = '/vcf/mine-ugur.vcf'

export const teamMineUgur: WrittenPage = {
  navLabel: 'Mine Uğur',
  title: 'Mine Uğur',
  description: `Contact Mine Uğur at Digitopia: ${EMAIL}, ${PHONE}.`,
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Digitopia',
      headline: 'Mine Uğur',
      align: 'center',
      ctas: [
        { label: 'Email', href: `mailto:${EMAIL}` },
        { label: 'Call', href: PHONE_HREF },
        { label: 'Save contact', href: VCARD },
      ],
      check:
        'No job title, biography or portrait was supplied with this request, so the ' +
        'page carries none. Send the title and a photograph and both go straight in.',
    },
    {
      // The grid variant, not `rows`: `rows` prints a body as plain text, and
      // the whole point of these two cards is that they are a mail window and
      // a dialled number rather than something to copy out by hand. The card
      // title is the value and the card itself is the link, which is how the
      // other team contact pages carry their details.
      kind: 'features',
      heading: 'Get in touch',
      columns: 2,
      items: [
        {
          title: EMAIL,
          note: 'Email',
          href: `mailto:${EMAIL}`,
        },
        {
          title: PHONE,
          note: 'Türkiye',
          href: PHONE_HREF,
        },
      ],
    },
    {
      kind: 'cta',
      heading: 'Rather start with the firm than a person?',
      sub: 'The contact form reaches the whole team, and About Us says who else you would be working with.',
      tone: 'band',
      ctas: [
        { label: 'Contact Digitopia', href: LINKS.contact },
        { label: 'About Digitopia', href: LINKS.about },
      ],
    },
  ],
}
